import {Component, EventEmitter, Input, Output} from '@angular/core';
import {TranslationService} from "../../core/services/translation.service";
import {ConfigService} from "../../core/services/config.service";
import {FormControl, FormGroup} from "@angular/forms";
import {HupSubscriptionsService} from "../../data/services/hup-subscriptions.service";
import {AddressListItem} from "../../data/models/address-list.model";
import {LocaleService} from "../../core/services/locale.service";
import {Address} from "../../data/models/address.model";
import {Person} from "../../data/models/person.model";
import {HouseNoPipe} from "../../core/pipes/houseno.pipe";
import {firstValueFrom} from 'rxjs';
import {CustomerService} from "../../data/services/customer.service";

@Component({
  selector: 'app-client-search',
  templateUrl: './client-search.component.html',
  styleUrls: ['./client-search.component.css'],
  providers: [HouseNoPipe]
})
export class ClientSearchComponent {
  @Output() validateButtonClick = new EventEmitter<number>();
  clientno: number;
  zipCode: string;
  houseNumber: string;
  validationFactors: string[];
  clientSelected: boolean = false;
  inputInvalid: boolean = false;
  zipCodeNlValid: boolean = false;
  houseNoNlValid: boolean = false;
  validationIsActive: boolean = true;
  validationSuccessful: boolean = false;
  onlyOneResult: boolean = true;
  clientnoNotFound: boolean = false;
  validationForm: FormGroup;
  customer: AddressListItem;
  customers: AddressListItem[];
  @Input() isLoading: boolean = false;
  defaultCountry: string;

  constructor(
    public translationService: TranslationService,
    private subscriptionService: HupSubscriptionsService,
    private customerService: CustomerService,
    private configService: ConfigService,
    private localeService: LocaleService,
    private houseNoPipe: HouseNoPipe
  ) {
    const locale = this.localeService.getDefaultCountry();
    this.defaultCountry = locale.split('_')[0];

    this.configService.loadConfig('client.validation-factors').subscribe(factors => {
      if (factors.value != '') {
        this.validationFactors = ((factors.value).replace(/ /g, "")).split(",");
      } else {
        this.validationIsActive = false;
      }
    });

    this.validationForm = new FormGroup<any>({
      firstname: new FormControl(''),
      lastname: new FormControl(''),
      street: new FormControl(''),
      zipcode: new FormControl(''),
      city: new FormControl(''),
      houseno: new FormControl('')
    });
  }

  async loadClient() {
    this.isLoading = true;
    if (this.clientno.toString() != '') {
      this.clientnoNotFound = false;
      this.onlyOneResult = true
      this.clientSelected = true;
    }
    this.isLoading = false;
  }

  async loadClientNl() {
    this.isLoading = true;
    if (this.zipCode && this.zipCode !== '' && this.houseNumber && this.houseNumber !== '') {
      this.clientSelected = true;
      const body = this.buildAddressListObject();
      body.address.zipcode = this.zipCode;
      body.address.houseno = this.houseNumber;
      this.subscriptionService.fillCustomerUnauthorized(body).subscribe(customers => {
        this.clientnoNotFound = false;
        if (customers && customers.length > 0) {
          this.onlyOneResult = customers.length === 1;
          if (customers.length === 1) {
            this.customer = customers[0];
            this.validateButtonClick.emit(this.customer.userBackendId);
          } else {
            // es kam mehr als einer Zurück, daher müssen wir noch mal die Zusatzfelder abfragen!
            this.customers = customers;
            //this.clientnoNotFound = true;
          }
        } else {
          this.clientnoNotFound = true;
        }
        this.isLoading = false;
      });
    } else {
      this.clientSelected = false;
    }
  }

  private buildAddressListObject(): AddressListItem {
    const address = new Address(null, null, null, null, null, null, null, null, null, null, null, null, null);
    const person = new Person(null, null, null, null, null, null, null, null)
    const addressListItem = new AddressListItem(null, address, person, null);
    return addressListItem;
  }

  async validateClientAndEmit() {
    if (this.validationIsActive) {
      this.isLoading = true;

      if (!this.customers) {
        const validFactors = this.validateCustomer(null);
        const valid = this.isEveryFactorValid(await validFactors);
        if (valid) {
          this.validateButtonClick.emit(this.clientno);
          this.inputInvalid = false;
        } else {
          this.inputInvalid = true;
        }
      } else if (!this.onlyOneResult) {
        // wir sind wohl in NL und müssen alle Adressen auf die ValidFactors prüfen..
        let foundValidCustomer: boolean = false;
        for (const customer of this.customers) {
          // nun jeden Eintrag überprüfen
          const validFactors = this.validateCustomer(customer);
          const valid = this.isEveryFactorValid(await validFactors);
          if (!foundValidCustomer) {
            foundValidCustomer = valid;
          }
          if (valid) {
            this.validateButtonClick.emit(customer.userBackendId);
            this.inputInvalid = false;
          }
        }
        if (!foundValidCustomer) {
          this.inputInvalid = true;
        }
      } else {
        this.inputInvalid = true;
      }
      this.validationSuccessful = !this.inputInvalid;
      this.isLoading = false;
    } else {
      // TODO Probleme im NL Mode! Dann ist Clientno nicht gesetzt!
      this.validateButtonClick.emit(this.clientno);
    }
  }

  private isEveryFactorValid(validFactors: boolean[]): boolean {
    return validFactors.every((value) => value == true);
  }

  async validateCustomer(customer: AddressListItem): Promise<boolean[]> {
    let validFactors: boolean[] = [];
    validFactors.splice(0, validFactors.length);
    for (let i = 0; i < this.validationFactors.length; i++) {
      if (customer != null) {
        validFactors.push(this.customerInformationIsValid(this.validationFactors.at(i), customer));
      } else {
        let validationFactor = this.validationFactors.at(i);
        let valid: boolean;
        let customerValue = this.validationForm.get(validationFactor).value;
        if (validationFactor === 'houseno') {
          customerValue = this.houseNoPipe.transform(customerValue);
        }
        validFactors.push(await firstValueFrom(
          this.customerService.validateCustomerUnauthorized(this.clientno, validationFactor, customerValue)
        ))
      }

    }

    return validFactors;
  }

  customerInformationIsValid(validationFactor: string, customer: AddressListItem): boolean {
    let customerValue: any;

    if (customer.person[validationFactor] != undefined) {
      customerValue = customer.person[validationFactor];
    } else if (customer.address[validationFactor] != undefined) {
      customerValue = customer.address[validationFactor];
    }

    if (validationFactor === 'houseno') {
      customerValue = this.houseNoPipe.transform(customerValue);
    }
    if(this.defaultCountry === 'NL' && validationFactor.endsWith('name')) {
      return customerValue.toString().toUpperCase() === this.validationForm.get(validationFactor).value.toString().toUpperCase();
    } else {
      return customerValue === this.validationForm.get(validationFactor).value;
    }
  }

  validateZipCodeNL(){
    this.zipCode = this.zipCode.toUpperCase();
    let value = this.zipCode.substring(0,4).replace(/[^0-9]*/g, '');
    this.zipCode = value + this.zipCode.substring(4,6).replace(/[^A-Za-z]*/g, '');
    this.zipCodeNlValid = ( /^\d{4}[A-Z]{2}$/g ).test(this.zipCode);
  }

  validateHouseNoNL() {
    this.houseNumber = this.houseNumber.substring(0,5).replace(/[^0-9]*/g, '');
    this.houseNoNlValid = ( /^\d{1,5}$/g ).test(this.houseNumber);
  }

  protected readonly document = document;
}
